import React from 'react';
import styled from '@emotion/styled';
import { IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { ExperienceContent } from '@/generated/graphql';
import { AccessIcon, SettingIcon, WhatIcon, ExpandIcon } from './propped-icons';

const MainDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: auto;
  padding: 0 20px 190px 20px;
`;
const CollapsibleDiv = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: ${props => (props.isCollapsed ? '#4c3b51' : '#f8dfcb')};
  height: auto;
  border-radius: 25px;
  padding: 12px;
  margin: 8px 0;
  width: 100%;
  color: ${props => (props.isCollapsed ? '#ffffff' : '#4c3b51')};
`;
const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
const IconNText = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  column-gap: 20px;
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans);
  font-weight: 600;
  font-size: 17px;
  line-height: 157.3%;
  color: inherit;
`;
const ExpandedText = styled(Text)`
  font-weight: normal;
  font-size: 18px;
  text-align: start;
  width: 100%;
  margin-top: 40px;
`;
const ExpandButton = styled(IconButton)`
  height: 34px;
  width: 34px;
  padding: 0;
  :hover {
    opacity: 0.8;
  }
`;

type CollapsibleButtonState = {
  firstRow?: boolean;
  secondRow?: boolean;
  thirdRow?: boolean;
};

export type CollapsibleProps = {
  content: ExperienceContent;
};

const Collapsible = ({ content }: CollapsibleProps) => {
  const [buttonState, setButtonState] = useState<CollapsibleButtonState>({
    firstRow: false,
    secondRow: false,
    thirdRow: false,
  });
  return (
    <MainDiv>
      <CollapsibleDiv
        isCollapsed={buttonState.firstRow!}
        onClick={() =>
          setButtonState({
            ...buttonState,
            firstRow: !buttonState.firstRow,
          })
        }
      >
        <Row>
          <IconNText>
            <AccessIcon isFocused={buttonState.firstRow!} />
            <Text>Accessibility</Text>
          </IconNText>
          <ExpandButton>
            <IconButton
              style={buttonState.firstRow ? { transform: 'rotate(180deg)' } : { transform: 'none' }}
            >
              <ExpandIcon isFocused={buttonState.firstRow!} />
            </IconButton>
          </ExpandButton>
        </Row>
        {buttonState.firstRow && <ExpandedText>{content.accessibility}</ExpandedText>}
      </CollapsibleDiv>
      <CollapsibleDiv
        isCollapsed={buttonState.secondRow!}
        onClick={() =>
          setButtonState({
            ...buttonState,
            secondRow: !buttonState.secondRow,
          })
        }
      >
        <Row>
          <IconNText>
            <WhatIcon isFocused={buttonState.secondRow!} />
            <Text>What to bring</Text>
          </IconNText>
          <ExpandButton>
            <IconButton
              style={
                buttonState.secondRow ? { transform: 'rotate(180deg)' } : { transform: 'none' }
              }
            >
              <ExpandIcon isFocused={buttonState.secondRow!} />
            </IconButton>
          </ExpandButton>
        </Row>
        {buttonState.secondRow && <ExpandedText>{content.bring}</ExpandedText>}
      </CollapsibleDiv>
      <CollapsibleDiv
        isCollapsed={buttonState.thirdRow!}
        onClick={() =>
          setButtonState({
            ...buttonState,
            thirdRow: !buttonState.thirdRow,
          })
        }
      >
        <Row>
          <IconNText>
            <SettingIcon isFocused={buttonState.thirdRow!} />
            <Text>The setting</Text>
          </IconNText>
          <ExpandButton>
            <IconButton
              style={buttonState.thirdRow ? { transform: 'rotate(180deg)' } : { transform: 'none' }}
            >
              <ExpandIcon isFocused={buttonState.thirdRow!} />
            </IconButton>
          </ExpandButton>
        </Row>
        {buttonState.thirdRow && <ExpandedText>{content.environment}</ExpandedText>}
      </CollapsibleDiv>
    </MainDiv>
  );
};

export default Collapsible;
